import React from 'react';

/**
 * Catches render-phase errors so a single bad component can't blank the
 * entire app.
 *
 * Without a boundary, React unmounts the whole root when any component
 * throws during render — the user sees a plain white page with no
 * explanation and no way back except a manual refresh. That is exactly
 * what a draw-request submission failure used to look like: the API
 * returned a 422 whose `detail` was an array, that array was handed to
 * `toast.error()`, and React threw "Objects are not valid as a React
 * child" while rendering the Toaster at the app root.
 *
 * The root-level boundary is the safety net. Individual surfaces should
 * still avoid throwing (see `lib/apiError.js`).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack in the console for support/debugging. Swallowing it
    // entirely would make these very hard to diagnose in production.
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 text-center">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-500 mb-4">
            This screen hit an unexpected error. Your data has not been lost —
            any work already saved is safe.
          </p>
          {error?.message && (
            <pre className="text-[11px] text-left text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 rounded-md p-2 mb-4 overflow-x-auto whitespace-pre-wrap break-words">
              {String(error.message)}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-2 text-sm rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
